import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { companiesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Building2, UserCheck, User, Eye, EyeOff, Briefcase } from 'lucide-react';
import { formatCPF as formatCPFUtil, formatCNPJ, cleanCNPJ } from '../lib/documentUtils';

// CPF formatting helper
const formatCPF = (value) => {
  return formatCPFUtil(value);
};

// CPF validation
const validateCPF = (cpf) => {
  const digits = cpf.replace(/\D/g, '');
  if (digits.length !== 11) return false;
  if (/^(\d)\1+$/.test(digits)) return false;
  
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += parseInt(digits[i]) * (10 - i);
  let d1 = (sum * 10 % 11) % 10;
  if (d1 !== parseInt(digits[9])) return false;
  
  sum = 0;
  for (let i = 0; i < 10; i++) sum += parseInt(digits[i]) * (11 - i);
  let d2 = (sum * 10 % 11) % 10;
  if (d2 !== parseInt(digits[10])) return false;
  
  return true;
};

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [searchParams] = useSearchParams();
  const defaultRole = searchParams.get('role') || 'CANDIDATE';
  
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registerType, setRegisterType] = useState(defaultRole === 'COMPANY' ? 'recruiter' : 'candidate');
  
  // Form para Candidato/Recrutador individual
  const [form, setForm] = useState({
    cpf: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: defaultRole
  });
  
  // Form para Empresa
  const [companyForm, setCompanyForm] = useState({
    companyName: '',
    cnpj: '',
    adminName: '',
    adminCpf: '',
    adminEmail: '',
    password: '',
    confirmPassword: ''
  });

  const handleCPFChange = (e) => {
    const formatted = formatCPF(e.target.value);
    setForm({ ...form, cpf: formatted });
  };
  
  const handleAdminCPFChange = (e) => {
    const formatted = formatCPF(e.target.value);
    setCompanyForm({ ...companyForm, adminCpf: formatted });
  };
  
  const handleCNPJChange = (e) => {
    const formatted = formatCNPJ(e.target.value);
    setCompanyForm({ ...companyForm, cnpj: formatted });
  };

  // Cadastro de Candidato ou Recrutador Individual
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateCPF(form.cpf)) {
      toast.error('CPF inválido');
      return;
    }
    
    if (form.password !== form.confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }
    
    if (form.password.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    setLoading(true);
    try {
      const role = registerType === 'candidate' ? 'CANDIDATE' : 'COMPANY';
      const user = await register(form.cpf, form.email, form.password, role);
      toast.success('Cadastro realizado com sucesso!');
      
      // Redirect to profile setup
      if (user.role === 'COMPANY') {
        navigate('/company/setup');
      } else {
        navigate('/candidate/setup');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar');
    } finally {
      setLoading(false);
    }
  };
  
  // Cadastro de Empresa com Usuário Master
  const handleCompanySubmit = async (e) => {
    e.preventDefault();
    
    // Validar CNPJ
    const cnpjClean = cleanCNPJ(companyForm.cnpj);
    if (cnpjClean.length !== 14) {
      toast.error('CNPJ inválido. Deve ter 14 caracteres.');
      return;
    }
    
    // Validar CPF do admin
    if (!validateCPF(companyForm.adminCpf)) {
      toast.error('CPF do usuário master inválido');
      return;
    }
    
    if (companyForm.password !== companyForm.confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }
    
    if (companyForm.password.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    
    if (!companyForm.companyName.trim()) {
      toast.error('Nome da empresa é obrigatório');
      return;
    }

    setLoading(true);
    try {
      // 1. Criar usuário master (tipo COMPANY)
      const user = await register(
        companyForm.adminCpf, 
        companyForm.adminEmail, 
        companyForm.password, 
        'COMPANY'
      );
      
      // 2. Criar empresa vinculada ao usuário
      await companiesAPI.create({
        nome: companyForm.companyName,
        cnpj: cnpjClean,
        plano: 'basic',
        selo_inclusivo: false
      });
      
      toast.success('Empresa cadastrada com sucesso!');
      navigate('/company');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar empresa');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1758518729240-7162d07427b8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBoYW5kc2hha2UlMjBidXNpbmVzcyUyMGRlYWx8ZW58MHx8fHwxNzcxOTAwMjQzfDA&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="absolute inset-0 bg-slate-900/70" />
        <div className="relative z-10 flex flex-col justify-center p-12">
          <div className="flex items-center gap-2 mb-8">
            <div className="w-12 h-12 bg-cyan-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">G2</span>
            </div>
            <span className="font-bold text-2xl text-white">G2eGente</span>
          </div>
          <h2 className="text-4xl font-bold text-white mb-4">
            Junte-se a nós
          </h2>
          <p className="text-lg text-slate-300">
            Crie sua conta e comece a conectar talentos de forma ética e transparente.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white overflow-y-auto">
        <div className="w-full max-w-md">
          <Button 
            variant="ghost" 
            className="mb-4"
            onClick={() => navigate('/')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>

          <Card className="border-slate-200 shadow-xl">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-bold text-slate-900">
                Criar Conta
              </CardTitle>
              <CardDescription>
                Escolha o tipo de conta que deseja criar
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Tabs para tipo de cadastro */}
              <Tabs value={registerType} onValueChange={setRegisterType} className="w-full">
                <TabsList className="grid w-full grid-cols-3 mb-4">
                  <TabsTrigger value="candidate" className="flex flex-col items-center gap-1 py-2 text-xs">
                    <UserCheck className="w-4 h-4" />
                    Candidato
                  </TabsTrigger>
                  <TabsTrigger value="recruiter" className="flex flex-col items-center gap-1 py-2 text-xs">
                    <Briefcase className="w-4 h-4" />
                    Recrutador
                  </TabsTrigger>
                  <TabsTrigger value="company" className="flex flex-col items-center gap-1 py-2 text-xs">
                    <Building2 className="w-4 h-4" />
                    Empresa
                  </TabsTrigger>
                </TabsList>
                
                {/* Tab: Candidato */}
                <TabsContent value="candidate">
                  <div className="mb-4 p-3 bg-cyan-50 border border-cyan-200 rounded-lg">
                    <p className="text-sm text-cyan-800">
                      <strong>Candidato:</strong> Crie seu perfil profissional e encontre oportunidades de emprego.
                    </p>
                  </div>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="cpf-candidate">CPF *</Label>
                      <Input
                        id="cpf-candidate"
                        type="text"
                        placeholder="000.000.000-00"
                        value={form.cpf}
                        onChange={handleCPFChange}
                        required
                        maxLength={14}
                        data-testid="register-cpf-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email-candidate">Email *</Label>
                      <Input
                        id="email-candidate"
                        type="email"
                        placeholder="seu@email.com"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        required
                        data-testid="register-email-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="password-candidate">Senha *</Label>
                      <div className="relative">
                        <Input
                          id="password-candidate"
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={form.password}
                          onChange={(e) => setForm({ ...form, password: e.target.value })}
                          required
                          data-testid="register-password-input"
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword-candidate">Confirmar Senha *</Label>
                      <div className="relative">
                        <Input
                          id="confirmPassword-candidate"
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={form.confirmPassword}
                          onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                          required
                          data-testid="register-confirm-password-input"
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-cyan-600 hover:bg-cyan-700"
                      disabled={loading}
                      data-testid="register-submit-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Cadastrando...
                        </>
                      ) : (
                        <>
                          <UserCheck className="w-4 h-4 mr-2" />
                          Criar Conta de Candidato
                        </>
                      )}
                    </Button>
                  </form>
                </TabsContent>
                
                {/* Tab: Recrutador Individual */}
                <TabsContent value="recruiter">
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Recrutador Individual:</strong> Profissional de RH autônomo ou freelancer que deseja publicar vagas.
                    </p>
                  </div>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="cpf-recruiter">CPF *</Label>
                      <Input
                        id="cpf-recruiter"
                        type="text"
                        placeholder="000.000.000-00"
                        value={form.cpf}
                        onChange={handleCPFChange}
                        required
                        maxLength={14}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email-recruiter">Email *</Label>
                      <Input
                        id="email-recruiter"
                        type="email"
                        placeholder="seu@email.com"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="password-recruiter">Senha *</Label>
                      <div className="relative">
                        <Input
                          id="password-recruiter"
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={form.password}
                          onChange={(e) => setForm({ ...form, password: e.target.value })}
                          required
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword-recruiter">Confirmar Senha *</Label>
                      <div className="relative">
                        <Input
                          id="confirmPassword-recruiter"
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={form.confirmPassword}
                          onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                          required
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Cadastrando...
                        </>
                      ) : (
                        <>
                          <Briefcase className="w-4 h-4 mr-2" />
                          Criar Conta de Recrutador
                        </>
                      )}
                    </Button>
                  </form>
                </TabsContent>
                
                {/* Tab: Empresa */}
                <TabsContent value="company">
                  <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <p className="text-sm text-purple-800">
                      <strong>Empresa:</strong> Cadastre sua empresa e crie um usuário master para gerenciar vagas e equipe.
                    </p>
                  </div>
                  <form onSubmit={handleCompanySubmit} className="space-y-4">
                    {/* Dados da Empresa */}
                    <div className="space-y-2">
                      <Label htmlFor="companyName">Nome da Empresa *</Label>
                      <Input
                        id="companyName"
                        type="text"
                        placeholder="Razão Social ou Nome Fantasia"
                        value={companyForm.companyName}
                        onChange={(e) => setCompanyForm({ ...companyForm, companyName: e.target.value })}
                        required
                        data-testid="register-company-name-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="cnpj">CNPJ *</Label>
                      <Input
                        id="cnpj"
                        type="text"
                        placeholder="AB.C12.345/6001-99"
                        value={companyForm.cnpj}
                        onChange={handleCNPJChange}
                        required
                        data-testid="register-cnpj-input"
                      />
                      <p className="text-xs text-slate-500">
                        Formato alfanumérico: AA.AAA.AAA/AAAA-00
                      </p>
                    </div>
                    
                    <div className="pt-2 border-t border-slate-200">
                      <p className="text-sm font-medium text-slate-700 mb-3">Usuário Master</p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="adminCpf">CPF do Responsável *</Label>
                      <Input
                        id="adminCpf"
                        type="text"
                        placeholder="000.000.000-00"
                        value={companyForm.adminCpf}
                        onChange={handleAdminCPFChange}
                        required
                        maxLength={14}
                        data-testid="register-admin-cpf-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="adminEmail">Email Corporativo *</Label>
                      <Input
                        id="adminEmail"
                        type="email"
                        placeholder="responsavel@empresa.com.br"
                        value={companyForm.adminEmail}
                        onChange={(e) => setCompanyForm({ ...companyForm, adminEmail: e.target.value })}
                        required
                        data-testid="register-admin-email-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="password-company">Senha *</Label>
                      <div className="relative">
                        <Input
                          id="password-company"
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={companyForm.password}
                          onChange={(e) => setCompanyForm({ ...companyForm, password: e.target.value })}
                          required
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword-company">Confirmar Senha *</Label>
                      <div className="relative">
                        <Input
                          id="confirmPassword-company"
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={companyForm.confirmPassword}
                          onChange={(e) => setCompanyForm({ ...companyForm, confirmPassword: e.target.value })}
                          required
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-purple-600 hover:bg-purple-700"
                      disabled={loading}
                      data-testid="register-company-submit-btn"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Cadastrando...
                        </>
                      ) : (
                        <>
                          <Building2 className="w-4 h-4 mr-2" />
                          Cadastrar Empresa
                        </>
                      )}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
              
              <div className="mt-6 text-center">
                <p className="text-sm text-slate-600">
                  Já tem uma conta?{' '}
                  <Link 
                    to="/login" 
                    className="text-cyan-600 hover:text-cyan-700 font-medium"
                  >
                    Entrar
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
